import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getActiveOrganization } from "selectors/organizations";
import { getPricesData } from "selectors/metaData";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import BusinessTypes from "const/BusinessTypes";
import Constants from "const/Constants";
import IntegrationServices from "const/IntegrationServices";
import Money from "utils/Money";
import React, { useCallback, useMemo } from "react";
import classNames from "classnames";

const REGULAR_FEATURES_ICONS = [
  [Icons.Devices, "#F3D434"],
  [Icons.Flag, "#0267D2"],
  [Icons.CheckSquareOffset, "#FA2F47"],
  [Icons.Robot, "#54D2CB"],
  [Icons.FileText, "#9550ED"],
  [Icons.FileLock, "#ED6135"],
  [Icons.CloudArrowDown, "#F4D94D"],
  [Icons.ChatsTeardrop, "#1DB173"],
  [Icons.SquaresFour, "#FA2F47"]
];

const ROBOTIC_FEATURES_ICONS = [
  [Icons.Lightning, "#FA2F47"],
  [Icons.CheckSquareOffset, "#F3D434"],
  [Icons.Eye, "#1DB173"],
  [Icons.Bank, "#ED6135"],
  [Icons.Atom, "#9550ED"],
  [Icons.CubeTransparent, "#54D2CB"]
];

const SelectServiceBlock = ({ businessType = BusinessTypes.REGULAR, onSelectService }) => {
  const { uiTexts, messages } = useSelector(getTextsData);

  const { currency, annualBilling, newPrices } = useSelector(getActiveOrganization);

  const { monthlyPrices, annualPrices } = useSelector(getPricesData);

  const prices = annualBilling ? annualPrices : monthlyPrices;

  const selectedProductName = businessType === BusinessTypes.ROBOTIC
    ? uiTexts.roboticAiBookkeeper
    : uiTexts.dataEntryAutomationHub;

  const [, ...regularWorkflowFeatures] = messages.onboardingRegularWorkflow;

  const [, , ...rpaWorkflowFeatures] = messages.onboardingRpaWorkflow;

  const selectedProductPrice = useMemo(() => {
    try {
      const selectedProductPrices = prices[businessType];

      const selectedCurrency = selectedProductPrices[currency] ? currency : Constants.DEFAULT_CURRENCY;

      const selectedPrice = selectedProductPrices[selectedCurrency];

      return Money.toMoneyString(selectedPrice, selectedCurrency, selectedPrice % 1 ? undefined : 0);
    } catch (error) {
      return null;
    }
  }, [businessType, currency, prices]);

  const pricePeriodLabel = useMemo(() => {
    return (annualBilling ? uiTexts.year : uiTexts.month).toLowerCase();
  }, [annualBilling, uiTexts]);

  const featureColumns = useMemo(() => {
    if (newPrices && businessType === BusinessTypes.ROBOTIC) {
      return [
        { key: BusinessTypes.REGULAR, features: regularWorkflowFeatures },
        { key: BusinessTypes.ROBOTIC, features: rpaWorkflowFeatures }
      ];
    }

    return [
      {
        key: businessType === BusinessTypes.ROBOTIC
          ? BusinessTypes.ROBOTIC
          : BusinessTypes.REGULAR,
        features: businessType === BusinessTypes.ROBOTIC
          ? rpaWorkflowFeatures
          : regularWorkflowFeatures
      }
    ];
  }, [newPrices, businessType, regularWorkflowFeatures, rpaWorkflowFeatures]);

  const handleButtonClick = useCallback(({ currentTarget: { dataset: { service } } }) => {
    onSelectService(service, selectedProductPrice, pricePeriodLabel);
  }, [selectedProductPrice, pricePeriodLabel, onSelectService]);

  return (
    <div className={Css.selectServiceBlock}>
      {!newPrices && <div className={Css.header}>{uiTexts.selectConnection}</div>}
      {newPrices && (
        <div className={Css.product}>
          <div className={Css.productTitle}>{selectedProductName}</div>
          {selectedProductPrice && (
            <div className={Css.price}>
              <span>{`${selectedProductPrice} / ${pricePeriodLabel}`}</span>
            </div>
          )}
          <div className={Css.featureColumns}>
            {featureColumns.map(({ key, features }) => {
              return (
                <div key={key} className={Css.featureColumn}>
                  {features.map((text, index) => {
                    const [Icon, color] = key === BusinessTypes.ROBOTIC
                      ? (ROBOTIC_FEATURES_ICONS[index] || [Icons.Check, "#000"])
                      : (REGULAR_FEATURES_ICONS[index] || [Icons.Check, "#000"]);

                    return (
                      <div key={`${key}_${text}`} className={Css.item}>
                        <Icon color={color} />
                        <div>{text}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {IntegrationServices.getFullIntegratedList().map(({ value, label, logo, availableForRpa, offlineIntegration }) => {
        return (businessType === BusinessTypes.REGULAR || availableForRpa) && (
          <div
            key={value}
            data-service={value}
            className={Css.service}
            onClick={handleButtonClick}>
            <img key={value} src={logo} title={label} alt={label} />
            <span>{label}</span>
            {businessType === BusinessTypes.ROBOTIC && !offlineIntegration && !availableForRpa && (
              <span className={classNames(Css.badge, Css.soonBadge)}>{uiTexts.soon}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(SelectServiceBlock);
